'use client'

import { Box, Stack } from '@mui/material'
import { getToolName } from 'ai'
import { useTranslations } from 'next-intl'

import ThinkingShimmer from '../parts/ThinkingShimmer'
import ToolCallPart, { isToolFailed, isToolRunning } from '../parts/ToolCallPart'
import toolCallLabel from '../stream/toolCalls'
import Disclosure from './Disclosure'
import StepLabel, { emphasiseVerb } from './StepLabel'
import toolRunSummary from './toolRunSummary'
import type { ToolRun } from './turnModel'

type ToolRunRowProps = {
  run: ToolRun
  /** The run is the step the turn is working on: its summary is in the present tense, with a status line below. */
  live?: boolean
  /** The turn ended, so a call without an output stopped before it finished. */
  interrupted?: boolean
}

/**
 * A run of tool calls as one summary line. A finished run opens to a row per call; a live run stays a plain line
 * with one status line under it, so nothing shifts while its counts change.
 */
export default function ToolRunRow({ run, live = false, interrupted = false }: ToolRunRowProps) {
  const t = useTranslations('features.AssistantV2')
  const failedCount = run.parts.filter(isToolFailed).length

  if (live) {
    return (
      <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
        <StepLabel>{toolRunSummary(run.counts, 'live', t)}</StepLabel>
        <RunStatus run={run} />
      </Stack>
    )
  }

  return (
    <Disclosure
      label={
        <StepLabel>
          {toolRunSummary(run.counts, 'past', t)}
          {failedCount > 0 && (
            <Box component="span" sx={{ color: 'error.main' }}>
              {` · ${t('runFailed', { count: failedCount })}`}
            </Box>
          )}
        </StepLabel>
      }
    >
      <Stack spacing={0.5}>
        {run.parts.map((part) => (
          <ToolCallPart key={part.toolCallId} part={part} interrupted={interrupted} />
        ))}
      </Stack>
    </Disclosure>
  )
}

/** The call in progress, or the shimmering "Thinking" while the model decides what to do next. */
function RunStatus({ run }: { run: ToolRun }) {
  const t = useTranslations('features.AssistantV2')
  const running = run.parts.findLast(isToolRunning)

  return (
    <Box data-testid="assistant-run-status">
      {running ? (
        <Box role="status">
          <StepLabel>
            {t.rich('runStatus', { label: toolCallLabel(getToolName(running), running.input), verb: emphasiseVerb })}
          </StepLabel>
        </Box>
      ) : (
        <ThinkingShimmer />
      )}
    </Box>
  )
}
